import { type ThemeColorName, THEME_COLOR_NAMES } from '@examdojo/styles';
import { GOJS_COLOR_PALETTE } from '../model';

export interface GojsResolvedColors {
  readonly bg: string;
  readonly text: string;
  readonly border: string;
}

export interface GojsStructuralColors {
  /** Default text color (neutral-500, was #706f6d) */
  readonly textDefault: string;
  /** Link / arrow color (neutral-400, was #8a8886) */
  readonly linkColor: string;
  /** Muted link color for hover-dimmed links (neutral-200, was #c9c8c6) */
  readonly mutedLinkColor: string;
  /** Default node fill (neutral-50, was #f7f5f2) */
  readonly defaultFill: string;
  /** Default node border (neutral-100, was #ebe9e6) */
  readonly defaultBorder: string;
  /** Grey stroke for dimmed elements (neutral-300, was #d1d5db) */
  readonly greyStroke: string;
  /** Grey text for dimmed elements (neutral-300, was #d1d5db) */
  readonly greyText: string;
  /** Grey fill base color, used with hexToRgba (neutral-400, was #9ca3af) */
  readonly greyFillBase: string;
  /** Palette shape stroke (neutral-200, was #d4d2cf) */
  readonly paletteStroke: string;
  /** Palette text (neutral-700, was #52504e) */
  readonly paletteText: string;
}

function resolveVar(style: CSSStyleDeclaration, color: string, shade: number): string {
  return style.getPropertyValue(`--${color}-${shade}`).trim();
}

/**
 * Resolve the node color palette for all theme colors from CSS variables.
 * Pattern: bg = shade 50, text = shade 500, border = shade 100.
 *
 * Falls back to static {@link GOJS_COLOR_PALETTE} values when CSS variables
 * are unavailable (tests, SSR).
 */
export function resolveGojsColorPalette(doc: Document): Record<ThemeColorName, GojsResolvedColors> {
  const style = getComputedStyle(doc.body);
  const palette = {} as Record<ThemeColorName, GojsResolvedColors>;

  for (const color of THEME_COLOR_NAMES) {
    const bg = resolveVar(style, color, 50);
    const text = resolveVar(style, color, 500);
    const border = resolveVar(style, color, 100);

    if (bg && text && border) {
      palette[color] = { bg, text, border };
    } else {
      // Fallback for environments without CSS variables
      const fallback = GOJS_COLOR_PALETTE[color];
      palette[color] = fallback ?? { bg: '#f7f5f2', text: '#8a8886', border: '#ebe9e6' };
    }
  }

  return palette;
}

/**
 * Resolve common structural colors used for links, arrows, and muted elements.
 * Falls back to light-mode defaults when CSS variables are unavailable.
 */
export function resolveGojsStructuralColors(doc: Document): GojsStructuralColors {
  const style = getComputedStyle(doc.body);

  return {
    textDefault: resolveVar(style, 'neutral', 500) || '#706f6d',
    linkColor: resolveVar(style, 'neutral', 400) || '#8a8886',
    mutedLinkColor: resolveVar(style, 'neutral', 200) || '#c9c8c6',
    defaultFill: resolveVar(style, 'neutral', 50) || '#f7f5f2',
    defaultBorder: resolveVar(style, 'neutral', 100) || '#ebe9e6',
    greyStroke: resolveVar(style, 'neutral', 300) || '#d1d5db',
    greyText: resolveVar(style, 'neutral', 300) || '#d1d5db',
    greyFillBase: resolveVar(style, 'neutral', 400) || '#9ca3af',
    paletteStroke: resolveVar(style, 'neutral', 200) || '#d4d2cf',
    paletteText: resolveVar(style, 'neutral', 700) || '#52504e',
  };
}
